import { Grid } from "@mui/material";
import useClientContext from "../../../../context/useClientContext";
import AgenticWorkflowsIcon from "../../../common/domain-icons/AgenticWorkflowsIcon";
import ApiGatewayIcon from "../../../common/domain-icons/ApiGatewayIcon";
import CorporateAccountingIcon from "../../../common/domain-icons/CorporateAccountingIcon";
import DataBackupIcon from "../../../common/domain-icons/DataBackupIcon";
import EntriliaMcpIcon from "../../../common/domain-icons/EntriliaMcpIcon";
import FundOperationsIcon from "../../../common/domain-icons/FundOperationsIcon";
import FundraisingIcon from "../../../common/domain-icons/FundraisingIcon";
import InvestorPortalIcon from "../../../common/domain-icons/InvestorPortalIcon";
import PortfolioMonitoringAppIcon from "../../../common/domain-icons/PortfolioMonitoringAppIcon";
import { pageRoutes } from "../../../../routes";
import IntegrationsCard from "./IntegrationsCard";

const AppsDashboard = () => {
  const {
    clientCode,
    hasAnyPermission,
    businessCentralApp,
    investorPortalApp,
    fundraisingApp,
    portfolioMonitoringApp,
    apiGatewayApp,
    dataBackupApp,
    entriliaMcpApp,
  } = useClientContext();

  const canConfigureInvestorPortal =
    hasAnyPermission(["ViewInvestorPortalSettings", "ManageInvestorPortalSettings"]) &&
    (investorPortalApp?.status === "Published" || investorPortalApp?.status === "NotConfigured");

  const canConfigureFundraisings =
    hasAnyPermission(["ViewFundraisings", "ManageFundraisings"]) &&
    (fundraisingApp?.status === "Published" || fundraisingApp?.status === "NotConfigured");

  const canManageIntegrations = hasAnyPermission(["ViewClientIntegrations", "ManageClientIntegrations"]);

  return (
    <Grid container spacing={3}>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--fund-operations"
          title="Fund Operations"
          Icon={FundOperationsIcon}
          appStatus={businessCentralApp?.fundAccountingEnabled ? businessCentralApp.status : "NotConnected"}
          text="Powerful dual-sided accounting, with intuitive event-driven automations tailored for private capital and other alternative asset funds."
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--corporate-accounting"
          title="Corporate Accounting"
          Icon={CorporateAccountingIcon}
          appStatus={businessCentralApp?.corporateAccountingEnabled ? businessCentralApp.status : "NotConnected"}
          text="Integrated corporate accounting that simplifies managing expenses, intercompany payments, and reimbursements across multiple entities and funds."
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--investor-portal"
          title="Investor Portal"
          Icon={InvestorPortalIcon}
          appStatus={investorPortalApp?.status ?? "NotConnected"}
          text="Modern tools for messaging and connectivity with your investors."
          configurationRoute={
            canConfigureInvestorPortal
              ? `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.investorPortalSettings}`
              : undefined
          }
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--fundraising"
          title="Fundraising"
          Icon={FundraisingIcon}
          appStatus={fundraisingApp?.status ?? "NotConnected"}
          text="Secure, customizable data rooms that simplify fundraising, offer analytics, and provide a seamless investor experience."
          configurationRoute={
            canConfigureFundraisings
              ? `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.fundraisings}`
              : undefined
          }
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--agentic-workflows"
          title="Agentic Workflows"
          Icon={AgenticWorkflowsIcon}
          appStatus="Published"
          text="AI-powered workflows that automate complex operational tasks across your firm."
          configurationRoute={
            canManageIntegrations
              ? `/${clientCode}/${pageRoutes.integration}/${pageRoutes.agenticWorkflows}`
              : undefined
          }
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--portfolio-monitoring"
          title="Portfolio Monitoring"
          Icon={PortfolioMonitoringAppIcon}
          appStatus={portfolioMonitoringApp?.status ?? "NotConnected"}
          text="Streamline data collection and track metrics to gain insights into the performance of your portfolio companies."
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--api-gateway"
          title="API Gateway"
          Icon={ApiGatewayIcon}
          appStatus={apiGatewayApp?.status ?? "NotConnected"}
          text="Enabling a 360 degree view of your data throughout your firm, while eliminating manual, high-risk data transfer methods."
          configurationRoute={
            canManageIntegrations ? `/${clientCode}/${pageRoutes.integration}/${pageRoutes.apiGateway}` : undefined
          }
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--data-backup"
          title="Data Backup"
          Icon={DataBackupIcon}
          appStatus={dataBackupApp?.status ?? "NotConnected"}
          text="Configure daily backups of your financial data to Amazon S3 for added risk mitigation and regulatory compliance."
          configurationRoute={
            canManageIntegrations ? `/${clientCode}/${pageRoutes.integration}/${pageRoutes.dataBackup}` : undefined
          }
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--entrilia-mcp"
          title="Entrilia MCP"
          Icon={EntriliaMcpIcon}
          appStatus={entriliaMcpApp?.status ?? "NotConnected"}
          text="The MCP Server allows AI assistants such as Claude, ChatGPT, and Cursor, as well as AI agents, to access and interact with your data."
          configurationRoute={
            canManageIntegrations ? `/${clientCode}/${pageRoutes.integration}/${pageRoutes.entriliaMcp}` : undefined
          }
        />
      </Grid>
    </Grid>
  );
};

export default AppsDashboard;
